import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { adminLogin, adminLogout, adminGetMe, getPrompts, updatePrompt, getAdminOverview, getAdminUsers, getAdminUserDetail, getAdminErrors, getHealthFull, getHealthHistory, triggerHealthRun } from "@/services/adminService";
import { toast, Toaster } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Lock, User, LogOut, Save, RefreshCw, FileText,
  Camera, Users, MapPin, Wand2, Film, ChevronRight,
  Search, Plus, Trash2, X, Check, AlertCircle,
  BarChart3, Activity, AlertTriangle, Clock, FolderOpen,
  MousePointer, UserCheck, TrendingUp, Eye,
  CheckCircle, XCircle, Zap, Database, Cpu, Play, Server
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";


// Category icons and colors
const CATEGORY_CONFIG = {
  characters: { icon: Users, color: "#d4a84b", label: "Characters" },
  locations: { icon: MapPin, color: "#14b8a6", label: "Locations" },
  storyboard: { icon: Camera, color: "#8b5cf6", label: "Storyboard" },
  analysis: { icon: Wand2, color: "#f43f5e", label: "AI Analysis" },
  script: { icon: FileText, color: "#3b82f6", label: "Script" },
  general: { icon: Film, color: "#6b7280", label: "General" }
};

// Login Component
function AdminLogin({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const data = await adminLogin({ username, password });
      toast.success("Welcome to Admin Console");
      onLogin(data);
    } catch (err) {
      setError("Invalid credentials");
      toast.error("Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0908] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-[#121110] border border-[#2a2826] rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-amber-500/20 to-amber-600/10 flex items-center justify-center">
              <Lock className="w-8 h-8 text-amber-400" />
            </div>
            <h1 className="text-2xl font-bold text-white">Admin Console</h1>
            <p className="text-sm text-gray-400 mt-2">Analytics & System Management</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Username</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="pl-10 bg-[#1a1918] border-[#2a2826] text-white placeholder:text-gray-600"
                  placeholder="Enter username"
                  autoFocus
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-[#1a1918] border-[#2a2826] text-white placeholder:text-gray-600"
                  placeholder="Enter password"
                />
              </div>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center gap-2 text-red-400 text-sm"
              >
                <AlertCircle className="w-4 h-4" />
                {error}
              </motion.div>
            )}

            <Button
              type="submit"
              disabled={loading || !username || !password}
              className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                "Login"
              )}
            </Button>
          </form>

          <p className="text-center text-xs text-gray-500 mt-6">
            1895 Admin Panel
          </p>
        </div>
      </motion.div>
    </div>
  );
}

// Stat Card Component
function StatCard({ icon: Icon, label, value, subValue, color = "#d4a84b" }) {
  return (
    <div className="bg-[#1a1918] border border-[#2a2826] rounded-xl p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-gray-400 mb-1">{label}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
          {subValue && <p className="text-xs text-gray-500 mt-1">{subValue}</p>}
        </div>
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: color + '20' }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
    </div>
  );
}

// Analytics Overview Component
function AnalyticsOverview() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const data = await getAdminOverview();
      setOverview(data.overview);
    } catch (err) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white">Analytics Overview</h2>
        <Button
          onClick={fetchOverview}
          variant="ghost"
          size="sm"
          className="text-gray-400 hover:text-white"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Total Users"
          value={overview?.total_users || 0}
          subValue={`+${overview?.users_this_week || 0} this week`}
          color="#d4a84b"
        />
        <StatCard
          icon={FolderOpen}
          label="Total Projects"
          value={overview?.total_projects || 0}
          subValue={`+${overview?.projects_today || 0} today`}
          color="#14b8a6"
        />
        <StatCard
          icon={Activity}
          label="Active Sessions (24h)"
          value={overview?.active_sessions_24h || 0}
          color="#8b5cf6"
        />
        <StatCard
          icon={Clock}
          label="Avg Session Duration"
          value={formatDuration(overview?.avg_session_duration_seconds)}
          color="#3b82f6"
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          icon={UserCheck}
          label="New Users Today"
          value={overview?.users_today || 0}
          color="#22c55e"
        />
        <StatCard
          icon={MousePointer}
          label="Total Events Tracked"
          value={overview?.total_events_tracked || 0}
          color="#f59e0b"
        />
        <StatCard
          icon={AlertTriangle}
          label="Unresolved Errors"
          value={overview?.unresolved_errors || 0}
          subValue={`${overview?.errors_this_week || 0} this week`}
          color="#ef4444"
        />
      </div>
    </div>
  );
}

// User List Component
function UsersList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, [page]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await getAdminUsers(page, 15);
      setUsers(data.users);
      setPagination(data.pagination);
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (selectedUser) {
    return <UserDetails userId={selectedUser} onBack={() => setSelectedUser(null)} />;
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white">Users ({pagination.total || 0})</h2>
        <Button
          onClick={fetchUsers}
          variant="ghost"
          size="sm"
          className="text-gray-400 hover:text-white"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : (
        <>
          <div className="bg-[#1a1918] border border-[#2a2826] rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2a2826]">
                  <th className="text-left p-3 text-xs font-medium text-gray-400">User</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-400">Joined</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-400">Last Login</th>
                  <th className="text-center p-3 text-xs font-medium text-gray-400">Projects</th>
                  <th className="text-center p-3 text-xs font-medium text-gray-400">Actions</th>
                  <th className="text-center p-3 text-xs font-medium text-gray-400">Errors</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.user_id} className="border-b border-[#2a2826] hover:bg-[#222120]">
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        {user.picture ? (
                          <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                            <User className="w-4 h-4 text-amber-400" />
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-white">{user.name || 'Unknown'}</p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-sm text-gray-400">{formatDate(user.created_at)}</td>
                    <td className="p-3 text-sm text-gray-400">{formatDate(user.last_login)}</td>
                    <td className="p-3 text-center">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-teal-500/20 text-teal-400">
                        {user.project_count}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400">
                        {user.action_count}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                        user.error_count > 0 ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {user.error_count}
                      </span>
                    </td>
                    <td className="p-3">
                      <Button
                        onClick={() => setSelectedUser(user.user_id)}
                        variant="ghost"
                        size="sm"
                        className="text-gray-400 hover:text-white"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                variant="ghost"
                size="sm"
                className="text-gray-400"
              >
                Previous
              </Button>
              <span className="text-sm text-gray-400">
                Page {page} of {pagination.pages}
              </span>
              <Button
                onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                disabled={page === pagination.pages}
                variant="ghost"
                size="sm"
                className="text-gray-400"
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// User Details Component
function UserDetails({ userId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  const fetchUserDetails = async () => {
    setLoading(true);
    try {
      const data = await getAdminUserDetail(userId);
      setData(data);
    } catch (err) {
      toast.error("Failed to load user details");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    return `${Math.round(seconds / 60)}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button onClick={onBack} variant="ghost" size="sm" className="text-gray-400">
          <ChevronRight className="w-4 h-4 rotate-180 mr-1" />
          Back
        </Button>
        <h2 className="text-xl font-semibold text-white">{data?.user?.name || 'User'}</h2>
      </div>

      {/* User Info */}
      <div className="bg-[#1a1918] border border-[#2a2826] rounded-xl p-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center">
            <User className="w-8 h-8 text-amber-400" />
          </div>
          <div>
            <p className="text-lg font-medium text-white">{data?.user?.name}</p>
            <p className="text-sm text-gray-400">{data?.user?.email}</p>
            <p className="text-xs text-gray-500">Joined: {formatDate(data?.user?.created_at)}</p>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-[#2a2826]">
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{data?.stats?.project_count || 0}</p>
            <p className="text-xs text-gray-400">Projects</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{data?.stats?.total_actions || 0}</p>
            <p className="text-xs text-gray-400">Actions</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{data?.stats?.total_sessions || 0}</p>
            <p className="text-xs text-gray-400">Sessions</p>
          </div>
        </div>
      </div>

      {/* Action Breakdown */}
      <div className="bg-[#1a1918] border border-[#2a2826] rounded-xl p-4">
        <h3 className="text-sm font-medium text-white mb-3">Action Breakdown</h3>
        <div className="space-y-2">
          {data?.action_breakdown?.slice(0, 10).map((action, i) => (
            <div key={i} className="flex items-center justify-between">
              <span className="text-sm text-gray-400">{action._id || 'Unknown'}</span>
              <span className="text-sm text-white font-medium">{action.count}</span>
            </div>
          ))}
          {(!data?.action_breakdown || data.action_breakdown.length === 0) && (
            <p className="text-sm text-gray-500">No actions recorded</p>
          )}
        </div>
      </div>

      {/* Recent Sessions */}
      <div className="bg-[#1a1918] border border-[#2a2826] rounded-xl p-4">
        <h3 className="text-sm font-medium text-white mb-3">Recent Sessions</h3>
        <div className="space-y-2">
          {data?.sessions?.map((session, i) => (
            <div key={i} className="flex items-center justify-between text-sm">
              <span className="text-gray-400">{formatDate(session.started_at)}</span>
              <span className="text-gray-300">{formatDuration(session.duration_seconds)}</span>
              <span className="text-gray-500">{session.actions_count || 0} actions</span>
            </div>
          ))}
          {(!data?.sessions || data.sessions.length === 0) && (
            <p className="text-sm text-gray-500">No sessions recorded</p>
          )}
        </div>
      </div>

      {/* Recent Errors */}
      {data?.recent_errors?.length > 0 && (
        <div className="bg-[#1a1918] border border-[#2a2826] rounded-xl p-4">
          <h3 className="text-sm font-medium text-red-400 mb-3">Recent Errors</h3>
          <div className="space-y-2">
            {data.recent_errors.map((error, i) => (
              <div key={i} className="p-2 bg-red-500/10 rounded border border-red-500/20">
                <p className="text-xs text-red-400">{error.error_type}</p>
                <p className="text-sm text-gray-300 truncate">{error.error_message}</p>
                <p className="text-xs text-gray-500">{formatDate(error.timestamp)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Error Logs Component
function ErrorLogs() {
  const [errors, setErrors] = useState([]);
  const [errorTypes, setErrorTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unresolvedOnly, setUnresolvedOnly] = useState(true);

  useEffect(() => {
    fetchErrors();
  }, [unresolvedOnly]);

  const fetchErrors = async () => {
    setLoading(true);
    try {
      const data = await getAdminErrors(unresolvedOnly);
      setErrors(data.errors);
      setErrorTypes(data.error_types);
    } catch (err) {
      toast.error("Failed to load errors");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white">Error Logs</h2>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-400">
            <input
              type="checkbox"
              checked={unresolvedOnly}
              onChange={(e) => setUnresolvedOnly(e.target.checked)}
              className="rounded border-gray-600 bg-[#1a1918]"
            />
            Unresolved only
          </label>
          <Button
            onClick={fetchErrors}
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-white"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Error Type Summary */}
      {errorTypes.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {errorTypes.map((type, i) => (
            <span 
              key={i}
              className="px-3 py-1 text-xs rounded-full bg-red-500/20 text-red-400"
            >
              {type._id}: {type.count}
            </span>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : errors.length === 0 ? (
        <div className="text-center py-12">
          <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <p className="text-gray-400">No errors found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {errors.map((error, i) => (
            <div 
              key={i}
              className="bg-[#1a1918] border border-[#2a2826] rounded-lg p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 text-xs rounded bg-red-500/20 text-red-400">
                      {error.error_type}
                    </span>
                    {error.action && (
                      <span className="text-xs text-gray-500">
                        during: {error.action}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-white mb-1">{error.error_message}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{formatDate(error.timestamp)}</span>
                    {error.page && <span>Page: {error.page}</span>}
                    {error.user_id && <span>User: {error.user_id.slice(0, 12)}...</span>}
                  </div>
                </div>
                {!error.resolved && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-green-400 hover:text-green-300"
                    onClick={async () => {
                      // Note: Would need to add _id to response for this to work
                      toast.info("Resolve functionality requires error ID");
                    }}
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {error.error_stack && (
                <details className="mt-2">
                  <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-400">
                    View stack trace
                  </summary>
                  <pre className="mt-2 p-2 bg-black/50 rounded text-xs text-gray-400 overflow-x-auto">
                    {error.error_stack}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Prompt Editor Component (existing)
function PromptEditor({ prompt, onSave, onCancel }) {
  const [content, setContent] = useState(prompt?.content || "");
  const [description, setDescription] = useState(prompt?.description || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updatePrompt(prompt.key, { content, description });
      
      toast.success("Prompt saved successfully");
      onSave({ ...prompt, content, description });
    } catch (err) {
      toast.error("Failed to save prompt");
    } finally {
      setSaving(false);
    }
  };

  const config = CATEGORY_CONFIG[prompt?.category] || CATEGORY_CONFIG.general;
  const Icon = config.icon;

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-[#2a2826] bg-[#121110]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: config.color + '20' }}
            >
              <Icon className="w-5 h-5" style={{ color: config.color }} />
            </div>
            <div>
              <h2 className="font-semibold text-white">{prompt?.name}</h2>
              <p className="text-xs text-gray-400">{prompt?.key}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={onCancel} variant="ghost" size="sm" className="text-gray-400 hover:text-white">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} size="sm" className="bg-amber-500 hover:bg-amber-600 text-black">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden p-4">
        <div className="h-full flex flex-col gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">Description</label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-[#1a1918] border-[#2a2826] text-white"
              placeholder="Brief description of this prompt"
            />
          </div>

          <div className="flex-1 flex flex-col min-h-0">
            <label className="block text-xs font-medium text-gray-400 mb-1.5">
              Prompt Content
              <span className="text-gray-600 ml-2">Use {"{variable}"} for placeholders</span>
            </label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="flex-1 bg-[#1a1918] border-[#2a2826] text-white font-mono text-sm resize-none"
              placeholder="Enter the system prompt..."
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// Prompts Manager Component
function PromptsManager() {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    setLoading(true);
    try {
      const data = await getPrompts();
      setPrompts(data.prompts || []);
    } catch (err) {
      toast.error("Failed to load prompts");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = (updatedPrompt) => {
    setPrompts(prompts.map(p => p.key === updatedPrompt.key ? updatedPrompt : p));
    setSelectedPrompt(null);
  };

  const filteredPrompts = prompts.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.key.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === "all" || p.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = ["all", ...new Set(prompts.map(p => p.category))];

  if (selectedPrompt) {
    return (
      <PromptEditor
        prompt={selectedPrompt}
        onSave={handleSave}
        onCancel={() => setSelectedPrompt(null)}
      />
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white">System Prompts</h2>
        <Button onClick={fetchPrompts} variant="ghost" size="sm" className="text-gray-400">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-[#1a1918] border-[#2a2826] text-white"
            placeholder="Search prompts..."
          />
        </div>
        <div className="flex gap-1">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilterCategory(cat)}
              className={`px-3 py-1.5 text-xs rounded-full transition-colors ${
                filterCategory === cat
                  ? 'bg-amber-500/20 text-amber-400'
                  : 'bg-[#2a2826] text-gray-400 hover:text-white'
              }`}
            >
              {cat === "all" ? "All" : CATEGORY_CONFIG[cat]?.label || cat}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : (
        <div className="grid gap-2">
          {filteredPrompts.map(prompt => {
            const config = CATEGORY_CONFIG[prompt.category] || CATEGORY_CONFIG.general;
            const Icon = config.icon;
            
            return (
              <motion.button
                key={prompt.key}
                onClick={() => setSelectedPrompt(prompt)}
                whileHover={{ x: 2 }}
                className="w-full p-4 rounded-lg text-left bg-[#1a1918] border border-[#2a2826] hover:border-amber-500/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: config.color + '15' }}
                  >
                    <Icon className="w-5 h-5" style={{ color: config.color }} />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white">{prompt.name}</p>
                    <p className="text-xs text-gray-500">{prompt.description || prompt.key}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                </div>
              </motion.button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Health Monitor ───────────────────────────────────────────────────────────

const SERVICE_STATUS_META = {
  operational: { color: "#22c55e", label: "Operational",    Icon: CheckCircle },
  degraded:    { color: "#f59e0b", label: "Degraded",       Icon: AlertTriangle },
  outage:      { color: "#ef4444", label: "Outage",         Icon: XCircle },
};

function svcMeta(status) {
  return SERVICE_STATUS_META[status] || { color: "#6b7280", label: status || "Unknown", Icon: RefreshCw };
}

// Icon mapping for service IDs
const SVC_ICONS = {
  api: Server,
  database: Database,
  ai_claude: Cpu,
  ai_gemini: Cpu,
};

// Latency bar — inline CSS, no external chart lib needed
function LatencyBar({ latency, maxMs = 2000 }) {
  if (!latency || latency <= 0) return null;
  const pct = Math.min((latency / maxMs) * 100, 100);
  const barColor = latency < 200 ? "#22c55e" : latency < 800 ? "#f59e0b" : "#ef4444";
  return (
    <div className="flex items-center gap-2 mt-2">
      <div className="flex-1 h-1.5 bg-[#2a2826] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, background: barColor }}
        />
      </div>
      <span className="text-xs font-mono text-gray-400 flex-shrink-0 w-14 text-right">{latency}ms</span>
    </div>
  );
}

// Expandable service card for admin health dashboard
function HealthServiceCard({ service, highlight }) {
  const [expanded, setExpanded] = useState(false);
  const meta = svcMeta(service.status);
  const { Icon: StatusIcon } = meta;
  const SvcIcon = SVC_ICONS[service.id] || Server;

  return (
    <div
      className="bg-[#1a1918] border rounded-xl p-4 transition-colors cursor-pointer hover:bg-[#1e1d1c]"
      style={{ borderColor: highlight ? meta.color + "50" : "#2a2826" }}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: meta.color + "15" }}
        >
          <SvcIcon className="w-4 h-4" style={{ color: meta.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-white truncate">{service.name}</p>
            <ChevronRight
              className="w-3 h-3 text-gray-500 transition-transform flex-shrink-0"
              style={{ transform: expanded ? "rotate(90deg)" : "rotate(0deg)" }}
            />
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <StatusIcon className="w-3 h-3" style={{ color: meta.color }} />
            <p className="text-xs" style={{ color: meta.color }}>{meta.label}</p>
          </div>
        </div>
        {service.latency_ms > 0 && (
          <span className="text-xs text-gray-400 font-mono flex-shrink-0 bg-[#121110] px-2 py-0.5 rounded">
            {service.latency_ms}ms
          </span>
        )}
      </div>

      {/* Latency bar */}
      <LatencyBar latency={service.latency_ms} />

      {/* Expandable details */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-[#2a2826] space-y-1.5">
          <div className="flex justify-between text-xs">
            <span className="text-gray-500">Status</span>
            <span style={{ color: meta.color }}>{service.status}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-500">Response Time</span>
            <span className="text-gray-300 font-mono">{service.latency_ms > 0 ? `${service.latency_ms}ms` : "N/A"}</span>
          </div>
          {service.endpoint && (
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Endpoint</span>
              <span className="text-gray-400 font-mono truncate ml-4">{service.endpoint}</span>
            </div>
          )}
          {service.message && (
            <div className="text-xs text-gray-500 mt-1 bg-[#121110] rounded p-2 font-mono">
              {service.message}
            </div>
          )}
          {service.error && (
            <div className="text-xs text-red-400 mt-1 bg-red-500/5 border border-red-500/20 rounded p-2 font-mono">
              {service.error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// History timeline bar — shows green/yellow/red for each check
function HistoryTimeline({ history }) {
  if (!history?.last_24h || history.last_24h.length === 0) {
    return (
      <p className="text-sm text-gray-500 text-center py-6">
        No history yet — data accumulates as the health checker runs.
      </p>
    );
  }

  // Build timeline blocks from last_24h data
  const blocks = history.last_24h.map((entry, i) => {
    // Determine overall status for this time point based on service latencies
    const hasOutage = Object.entries(entry).some(([k, v]) => k !== "time" && v === -1);
    const hasDegraded = Object.entries(entry).some(([k, v]) => k !== "time" && typeof v === "number" && v > 1000);
    const color = hasOutage ? "#ef4444" : hasDegraded ? "#f59e0b" : "#22c55e";
    const t = new Date(entry.time);
    const label = t.toLocaleString(undefined, { hour: "2-digit", minute: "2-digit" });
    return { color, label, time: entry.time };
  });

  return (
    <div>
      <div className="flex gap-0.5 items-end" style={{ height: 32 }}>
        {blocks.map((block, i) => (
          <div
            key={i}
            className="flex-1 rounded-sm transition-colors group relative"
            style={{ background: block.color, minWidth: 2, height: "100%", opacity: 0.8 }}
            title={`${block.label}`}
          />
        ))}
      </div>
      <div className="flex justify-between mt-1.5">
        <span className="text-[10px] text-gray-600">24h ago</span>
        <span className="text-[10px] text-gray-600">Now</span>
      </div>
    </div>
  );
}

function HealthMonitor() {
  const [full, setFull] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [f, h] = await Promise.all([getHealthFull(), getHealthHistory()]);
      setFull(f);
      setHistory(h);
      setLastRefresh(new Date());
    } catch (err) {
      toast.error("Failed to load health data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 30_000); // 30 second refresh
    return () => clearInterval(iv);
  }, [fetchData]);

  const handleTriggerRun = async () => {
    setRunning(true);
    try {
      await triggerHealthRun();
      toast.success("Health check triggered — refreshing...");
      await fetchData();
    } catch (err) {
      toast.error("Failed to trigger health run");
    } finally {
      setRunning(false);
    }
  };

  const overallColor = full?.status === "operational" ? "#22c55e"
    : full?.status === "major_outage" ? "#ef4444"
    : "#f59e0b";
  const overallLabel = full?.status === "operational" ? "All Systems Operational"
    : full?.status === "major_outage" ? "Major Outage"
    : full?.status === "partial_outage" ? "Partial Outage"
    : "Checking...";

  const allServices = full?.services || [];
  const aiServices  = allServices.filter(s => s.id === "ai_claude" || s.id === "ai_gemini");
  const dbService   = allServices.find(s => s.id === "database");
  const otherSvcs   = allServices.filter(s => s.id !== "ai_claude" && s.id !== "ai_gemini");

  // Find max latency for bar scaling
  const maxLatency = Math.max(500, ...allServices.map(s => s.latency_ms || 0));

  const CHART_LINES = [
    { id: "api",       name: "API",        color: "#d4a84b" },
    { id: "database",  name: "Database",   color: "#22c55e" },
    { id: "ai_claude", name: "AI Claude",  color: "#a78bfa" },
    { id: "ai_gemini", name: "AI Gemini",  color: "#38bdf8" },
  ];

  const formatHour = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString(undefined, { hour: "2-digit", minute: "2-digit" });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Health Monitor</h2>
          {lastRefresh && (
            <p className="text-xs text-gray-500 mt-0.5">
              Last refresh: {lastRefresh.toLocaleTimeString()} &middot; Auto-refreshes every 30s
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={fetchData}
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-white"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button
            onClick={handleTriggerRun}
            disabled={running}
            size="sm"
            className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
          >
            {running ? (
              <RefreshCw className="w-4 h-4 animate-spin mr-1" />
            ) : (
              <Play className="w-4 h-4 mr-1" />
            )}
            Run Health Check
          </Button>
        </div>
      </div>

      {/* Overall Status Banner */}
      <div
        className="rounded-xl p-5 flex items-center gap-4 border"
        style={{
          background: overallColor + "10",
          borderColor: overallColor + "30",
        }}
      >
        {full?.status === "operational" ? (
          <CheckCircle className="w-8 h-8 flex-shrink-0" style={{ color: overallColor }} />
        ) : (
          <AlertTriangle className="w-8 h-8 flex-shrink-0" style={{ color: overallColor }} />
        )}
        <div className="flex-1">
          <p className="text-lg font-bold" style={{ color: overallColor }}>{overallLabel}</p>
          {full?.checked_at && (
            <p className="text-xs text-gray-500 mt-0.5">
              Checked: {new Date(full.checked_at).toLocaleString()}
            </p>
          )}
        </div>
        {/* Quick latency summary */}
        <div className="hidden sm:flex items-center gap-4">
          {allServices.filter(s => s.latency_ms > 0).slice(0, 4).map(s => (
            <div key={s.id} className="text-center">
              <p className="text-lg font-bold text-white font-mono">{s.latency_ms}</p>
              <p className="text-[10px] text-gray-500 uppercase">{s.name?.split(" ")[0]}</p>
            </div>
          ))}
        </div>
      </div>

      {/* AI Services — Claude & Gemini */}
      {aiServices.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Zap className="w-3.5 h-3.5" /> External AI APIs
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {aiServices.map(svc => (
              <HealthServiceCard key={svc.id} service={svc} highlight />
            ))}
          </div>
        </div>
      )}

      {/* MongoDB */}
      {dbService && (
        <div>
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Database className="w-3.5 h-3.5" /> Database
          </h3>
          <div className="bg-[#1a1918] border border-[#2a2826] rounded-xl p-4">
            <div className="flex items-center gap-4">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ background: svcMeta(dbService.status).color + "20" }}
              >
                <Database className="w-5 h-5" style={{ color: svcMeta(dbService.status).color }} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-white">MongoDB</p>
                <p className="text-xs mt-0.5" style={{ color: svcMeta(dbService.status).color }}>
                  {svcMeta(dbService.status).label}
                </p>
              </div>
              {dbService.latency_ms > 0 && (
                <div className="text-right">
                  <p className="text-xl font-bold text-white font-mono">{dbService.latency_ms}ms</p>
                  <p className="text-xs text-gray-500">query latency</p>
                </div>
              )}
            </div>
            <LatencyBar latency={dbService.latency_ms} maxMs={maxLatency} />
          </div>
        </div>
      )}

      {/* All Services Grid — expandable cards */}
      <div>
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Server className="w-3.5 h-3.5" /> All Services
          <span className="text-gray-600 font-normal normal-case tracking-normal ml-1">(click to expand)</span>
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {otherSvcs.map(svc => (
            <HealthServiceCard key={svc.id} service={svc} highlight={svc.status !== "operational"} />
          ))}
        </div>
      </div>

      {/* 24h Timeline */}
      <div>
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Clock className="w-3.5 h-3.5" /> 24h Status Timeline
        </h3>
        <div className="bg-[#1a1918] border border-[#2a2826] rounded-xl p-4">
          <HistoryTimeline history={history} />
          <div className="flex items-center gap-4 mt-3 justify-center">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm" style={{ background: "#22c55e" }} />
              <span className="text-[10px] text-gray-500">Healthy</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm" style={{ background: "#f59e0b" }} />
              <span className="text-[10px] text-gray-500">Degraded</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm" style={{ background: "#ef4444" }} />
              <span className="text-[10px] text-gray-500">Outage</span>
            </div>
          </div>
        </div>
      </div>

      {/* Historical Chart */}
      <div>
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Activity className="w-3.5 h-3.5" /> Response Time — Last 24h
        </h3>
        <div className="bg-[#1a1918] border border-[#2a2826] rounded-xl p-4">
          {(!history?.last_24h || history.last_24h.length === 0) ? (
            <p className="text-sm text-gray-500 text-center py-10">
              No history yet — data accumulates as the health checker runs.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={history.last_24h} margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis
                  dataKey="time"
                  tickFormatter={formatHour}
                  tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 11 }}
                  axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  unit="ms"
                />
                <Tooltip
                  contentStyle={{
                    background: "#121110",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  labelFormatter={formatHour}
                  formatter={(val, name) => [`${val}ms`, name]}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }} />
                {CHART_LINES.map(l => (
                  <Line
                    key={l.id}
                    type="monotone"
                    dataKey={l.id}
                    name={l.name}
                    stroke={l.color}
                    strokeWidth={1.5}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Uptime Table */}
      <div>
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <TrendingUp className="w-3.5 h-3.5" /> Uptime
        </h3>
        <div className="bg-[#1a1918] border border-[#2a2826] rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#2a2826]">
                <th className="text-left p-3 text-xs font-medium text-gray-400">Service</th>
                <th className="text-right p-3 text-xs font-medium text-gray-400">7 days</th>
                <th className="text-right p-3 text-xs font-medium text-gray-400">30 days</th>
              </tr>
            </thead>
            <tbody>
              {(history?.uptime || []).map((entry) => {
                const c7  = entry.uptime_7d  >= 99.9 ? "#22c55e" : entry.uptime_7d  >= 99 ? "#f59e0b" : "#ef4444";
                const c30 = entry.uptime_30d >= 99.9 ? "#22c55e" : entry.uptime_30d >= 99 ? "#f59e0b" : "#ef4444";
                return (
                  <tr key={entry.id} className="border-b border-[#2a2826] last:border-0">
                    <td className="p-3 text-sm text-gray-300">{entry.name}</td>
                    <td className="p-3 text-right text-sm font-mono" style={{ color: c7 }}>
                      {entry.uptime_7d.toFixed(2)}%
                    </td>
                    <td className="p-3 text-right text-sm font-mono" style={{ color: c30 }}>
                      {entry.uptime_30d.toFixed(2)}%
                    </td>
                  </tr>
                );
              })}
              {(!history?.uptime || history.uptime.length === 0) && (
                <tr>
                  <td colSpan={3} className="p-4 text-center text-sm text-gray-500">No uptime data yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Incidents */}
      {history?.incidents?.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" /> Recent Incidents
          </h3>
          <div className="space-y-2">
            {history.incidents.map((inc, i) => (
              <div key={i} className="bg-[#1a1918] border border-red-500/20 rounded-xl p-4 flex items-start gap-3">
                <div
                  className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                  style={{ background: inc.status === "major_outage" ? "#ef4444" : "#f59e0b" }}
                />
                <div>
                  <p className="text-sm font-medium" style={{ color: inc.status === "major_outage" ? "#ef4444" : "#f59e0b" }}>
                    {inc.status === "major_outage" ? "Major Outage" : "Partial Outage"}
                  </p>
                  {inc.affected?.length > 0 && (
                    <p className="text-xs text-gray-400 mt-0.5">{inc.affected.join(", ")}</p>
                  )}
                  <p className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(inc.timestamp).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Main Admin Dashboard with Tabs
function AdminDashboard({ onLogout }) {
  const [activeTab, setActiveTab] = useState("overview");

  const tabs = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "users", label: "Users", icon: Users },
    { id: "errors", label: "Errors", icon: AlertTriangle },
    { id: "prompts", label: "Prompts", icon: FileText },
    { id: "health", label: "Health", icon: Activity },
  ];

  const handleLogout = async () => {
    try {
      await adminLogout();
    } catch (err) {}
    onLogout();
  };

  return (
    <div className="h-screen bg-[#0a0908] flex flex-col overflow-hidden">
      {/* Top Navigation */}
      <header className="border-b border-[#2a2826] bg-[#121110] flex-shrink-0">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500/20 to-amber-600/10 flex items-center justify-center">
                <Lock className="w-4 h-4 text-amber-400" />
              </div>
              <h1 className="font-bold text-white">1895 Admin</h1>
            </div>
            
            {/* Tabs */}
            <nav className="flex items-center gap-1 ml-8">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'bg-amber-500/20 text-amber-400'
                        : 'text-gray-400 hover:text-white hover:bg-[#1a1918]'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          <Button onClick={handleLogout} variant="ghost" size="sm" className="text-gray-400 hover:text-white">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === "overview" && <AnalyticsOverview />}
            {activeTab === "users" && <UsersList />}
            {activeTab === "errors" && <ErrorLogs />}
            {activeTab === "prompts" && <PromptsManager />}
            {activeTab === "health" && <HealthMonitor />}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}

// Main Admin Page Component
export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      await adminGetMe();
      setIsAuthenticated(true);
    } catch (err) {
      setIsAuthenticated(false);
    } finally {
      setChecking(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0a0908] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-right" theme="dark" />
      {isAuthenticated ? (
        <AdminDashboard onLogout={() => setIsAuthenticated(false)} />
      ) : (
        <AdminLogin onLogin={() => setIsAuthenticated(true)} />
      )}
    </>
  );
}
